import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { IArmador } from './ArmadoresServices'
import { IEmbarcacion } from './EmbarcacionesServices'
import { IProveedor } from './ProveedoresServices'
import { IRequest } from './RequestsServices'
import { IServiceVehicle } from './ServiceVehiclesServices'

export enum EmbarcacionType {
  PILOTAJE = 'pilotaje',
  AUXILIAR = 'auxiliar',
  FLETERA = 'fletera',
}

export enum ServiceType {
  PERTRECHOS = 'Pertrechos',
  VIVERES = 'Víveres',
  REPUESTOS = 'Repuestos',
  CAMBIO_TRIPULACION = 'Cambio de tripulación',
  VISITA_ORGANISMO_FISCALIZADOR = 'Visita de organismo fiscalizador',
  VISITA_NAVE = 'Visita a la nave',
  OTRO = 'Otro',
}

export const ServicioExpirado = (service: IService): boolean => {
  const prevDate = new Date().getTime() - 1000 * 60 * 60 * 10
  const postDate = new Date().getTime() + 1000 * 60 * 60 * 10
  // Detectar fecha de inicio, si el dia ya paso y tiene hora 00:00 ya esta expirado
  const serviceStartDate = new Date(service.fecha_inicio)
  const serviceYear = serviceStartDate.getFullYear()
  const serviceMonth = serviceStartDate.getMonth()
  const serviceDay = serviceStartDate.getDate()

  // Comparar solo fechas sin horas
  const today = new Date()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth()
  const todayDay = today.getDate()

  const serviceDateOnly = new Date(serviceYear, serviceMonth, serviceDay).getTime()
  const todayDateOnly = new Date(todayYear, todayMonth, todayDay).getTime()

  return serviceDateOnly < todayDateOnly
}

export const ServicioReservado = (service: IService): boolean => {
  const postDate = new Date().getTime() + 1000 * 60 * 60 * 10

  const serviceStartDate = new Date(service.fecha_inicio)
  const serviceYear = serviceStartDate.getFullYear()
  const serviceMonth = serviceStartDate.getMonth()
  const serviceDay = serviceStartDate.getDate()

  // Detectar si la fecha de inicio es después de hoy
  const today = new Date()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth()
  const todayDay = today.getDate()

  const serviceDateOnly = new Date(serviceYear, serviceMonth, serviceDay).getTime()
  const todayDateOnly = new Date(todayYear, todayMonth, todayDay).getTime()

  return serviceDateOnly > todayDateOnly || serviceStartDate.getTime() > postDate
}

export interface IService {
  id: number
  tipo_servicio: 'armador' | 'proveedor'
  id_embarcacion_prestadora: number | null
  tipo_embarcacion: EmbarcacionType | null
  fecha_inicio: string
  fecha_termino: string
  motivo_solicitud: ServiceType | null
  fecha_registro: string
  fecha_actualizacion: string
  usuario_registro: number
  activo: boolean
  observaciones: string | null
  nave_atendida?: string | null
}

export interface OperatedService extends IService {
  requestData: IRequest | undefined
  posicionamientosData: Array<IRequest>
  tarifasEspecialesData: Array<IRequest>
  vehiculosData: IServiceVehicle[]
  vehiculosRequestsData: IRequest[]
  usuarioSolicitanteData: IArmador | IProveedor | undefined
  embarcacionData: IEmbarcacion | undefined
}

export const defaultIService: IService = {
  id: 0,
  tipo_servicio: 'armador',
  id_embarcacion_prestadora: null,
  tipo_embarcacion: null,
  fecha_inicio: new Date().toISOString(),
  fecha_termino: new Date().toISOString(),
  motivo_solicitud: null,
  fecha_registro: new Date().toISOString(),
  fecha_actualizacion: new Date().toISOString(),
  usuario_registro: 0,
  activo: true,
  observaciones: null,
  nave_atendida: null,
}

export default class ServicesServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para tripulantes
    this.bucketID = dataStorageManager.registerBucket<IService>(
      this.getAllServices.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.SERVICES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertService',
            {
              operation: this.insertService.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateService',
            {
              operation: this.updateService.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllServices(): Promise<IService[]> {
    try {
      const response = await dataQueryManager.addQuery<IService | IService[]>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_select_servicios_armador_proveedor`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      let list: IService[] = []
      if (response.data && Array.isArray(response.data)) {
        list = response.data
      } else if (response.data) {
        list = [response.data]
      }
      return list.map((item) => {
        if (!item.nave_atendida && item.observaciones) {
          try {
            const parsed = JSON.parse(item.observaciones)
            if (parsed && typeof parsed === 'object' && parsed.nave_atendida !== undefined) {
              item.nave_atendida = parsed.nave_atendida
            }
          } catch {
            const match = String(item.observaciones).match(/NAVE:\s*([^|]+)/)
            if (match) {
              item.nave_atendida = match[1].trim()
            }
          }
        }
        return item
      })
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertService(
    serviceData: Omit<IService, 'id' | 'fecha_registro' | 'fecha_actualizacion'>,
  ): Promise<number | false> {
    try {
      let observacionesPayload = serviceData.observaciones
      if (serviceData.nave_atendida) {
        try {
          const parsed = serviceData.observaciones ? JSON.parse(serviceData.observaciones) : {}
          parsed.nave_atendida = serviceData.nave_atendida
          observacionesPayload = JSON.stringify(parsed)
        } catch {
          observacionesPayload = JSON.stringify({
            code: serviceData.observaciones,
            nave_atendida: serviceData.nave_atendida,
          })
        }
      }

      const body = {
        op_insert_servicios_armador_proveedor: {
          tipo_servicio: serviceData.tipo_servicio,
          id_embarcacion_prestadora: serviceData.id_embarcacion_prestadora,
          tipo_embarcacion: serviceData.tipo_embarcacion,
          fecha_inicio: serviceData.fecha_inicio,
          fecha_termino: serviceData.fecha_termino,
          motivo_solicitud: serviceData.motivo_solicitud,
          usuario_registro: serviceData.usuario_registro,
          activo: serviceData.activo,
          observaciones: observacionesPayload,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_insert_servicios_armador_proveedor`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      // op_insert_servicios_armador_proveedor devuelve el id generado (RETURNING): lo propagamos.
      if (response.data && typeof response.data === 'number') {
        return response.data
      }
    } catch (error) {
      console.error('Error inserting embarcacion:', error)
    }
    return false
  }

  async updateService(serviceData: IService): Promise<boolean> {
    try {
      let observacionesPayload = serviceData.observaciones
      if (serviceData.nave_atendida) {
        try {
          const parsed = serviceData.observaciones ? JSON.parse(serviceData.observaciones) : {}
          parsed.nave_atendida = serviceData.nave_atendida
          observacionesPayload = JSON.stringify(parsed)
        } catch {
          observacionesPayload = JSON.stringify({
            code: serviceData.observaciones,
            nave_atendida: serviceData.nave_atendida,
          })
        }
      }

      const body = {
        op_update_servicios_armador_proveedor: {
          tipo_servicio: serviceData.tipo_servicio,
          id_embarcacion_prestadora: serviceData.id_embarcacion_prestadora,
          tipo_embarcacion: serviceData.tipo_embarcacion,
          fecha_inicio: serviceData.fecha_inicio,
          fecha_termino: serviceData.fecha_termino,
          motivo_solicitud: serviceData.motivo_solicitud,
          activo: serviceData.activo,
          observaciones: observacionesPayload,
          id: serviceData.id,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_update_servicios_armador_proveedor`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating viaje:', error)
    }
    return false
  }
}
